/**
 * Phase2WeeklyGrid.tsx
 * FEAT-2026-0007 Phase 6C.2 — Task × Week weekly allocation grid.
 *
 * Section 2 of the Phase-2 employee planning view.
 *
 * Layout:
 *   - Sticky first column: Task Name.
 *   - Header row: "WEEK n · Sat DD Mon – Fri DD Mon" for each BST week in
 *     the span from the earliest task start to the latest task end.
 *   - One data row per ScheduleTask; each cell = Phase2WeeklyCell.
 *
 * Column computation:
 *   - Min start = min(task.StartDate) across all tasks that have bookings.
 *   - Max end   = max(task.EndDate)   across all tasks that have bookings.
 *   - Weeks = weekStartsInRange(minStart, maxEnd) — BST Saturday-start weeks.
 *
 * Assignment data (Phase 8.1 — REQ-07 single source of truth):
 *   - assignmentMap is passed in from Phase2Panel which owns it via
 *     useTaskAssignments.  This grid NO LONGER fetches its own assignments —
 *     it was a separate fetch from a different endpoint that produced a
 *     separate React state, causing the top and bottom panels to desync.
 *   - Phase2WeeklyCell receives the full assignment list for its task; it
 *     filters by weekStart internally.
 *   - Mutations call onAssignmentsChanged(taskKey) which flows up to
 *     Phase2Panel.reloadOneTaskAssignees → useTaskAssignments.reloadOne.
 *     The updated assignmentMap then propagates to BOTH panels at once.
 *
 * Editing:
 *   - WorkloadBar drag-end → updateEmployeeAssignment (inside Phase2WeeklyCell).
 *   - Remove (×) → deleteEmployeeAssignment (inside Phase2WeeklyCell).
 *   - Add (+) → InlineAssigneePicker (inside Phase2WeeklyCell); on success,
 *     cell calls onAssignmentsChanged → shared state refreshes both panels.
 *
 * Scope: 6C.2 only. Week-boundary split and drag-extend are 6C.3.
 *
 * ≤600 lines — rendering split to Phase2WeeklyCell.tsx.
 */

import React, { useCallback, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { useTheme, alpha } from '@mui/material/styles';

import { Phase2WeeklyCell } from './Phase2WeeklyCell';
import { displayTaskName, showTypeSubtitle } from './taskDisplayName';
import { AssignEmployeesDialog } from '../../tasks/AssignEmployeesDialog';
import { useBaselineAssignments } from './useBaselineAssignments';
import type { TaskWithBookings } from '../../../../services/scheduleTasks';
import type { AssignableUser } from '../../../../services/scheduleAssignments';
import type { AssignmentMap } from './useTaskAssignments';
import { weekStartsInRange } from '../../../../utils/weekUtils';
import { RAIL_GUTTER_W, RAIL_WEEK_W } from './phase2RailGeometry';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface Phase2WeeklyGridProps {
  tasks: TaskWithBookings[];
  /**
   * Phase 8.1 — Single source of truth for assignment data (REQ-07).
   * Passed down from Phase2Panel via useTaskAssignments. This grid no longer
   * fetches its own assignments so that the top task grid (InlineAssigneePicker
   * chips) and this bottom weekly grid always reflect the same state.
   */
  assignmentMap: AssignmentMap;
  scenarioKey: string | null;
  /**
   * Phase 8.1 — Called after any assignment mutation (add / remove / drag)
   * inside a cell. Flows up to Phase2Panel.reloadOneTaskAssignees →
   * useTaskAssignments.reloadOne so BOTH panels refresh from a single fetch.
   */
  onAssignmentsChanged: (taskKey: string) => void;
  /**
   * Called after any allocation mutation that changes stored capacity totals
   * (percent change, add, remove, booking resize). The parent uses this to
   * increment refreshKey on Phase2CapacityTable (table 3 live recompute — 6C.5).
   */
  onAllocationChanged?: () => void;
  /**
   * 6C.6 — When true, fetch baseline (no-scenarioKey) assignments and render
   * ghosted "baseline: N%" sublabels in each cell behind the scenario bars.
   * When false (default), no extra fetch is performed.
   */
  compareToBaseline?: boolean;
  /**
   * The week (Saturday ISO) currently hovered anywhere on the shared rail, so this
   * grid can light up the SAME week as the capacity grid below (the synchronized
   * "beam"). Null = none hovered (2026-06-23).
   */
  hoveredWeek?: string | null;
  /** Report the week the pointer is over (or null on leave) for the shared beam. */
  onHoverWeek?: (weekStart: string | null) => void;
  /** ISO stat-holiday dates — forwarded to each booking bar's mini week strip so a covered
   *  weekend / stat holiday greys out instead of reading as a worked day. */
  holidays?: string[];
}

// Shared rail geometry (2026-06-23): these MUST match the capacity grid so the two
// stacked panels line up week-for-week. gutter = RAIL_GUTTER_W; each week = RAIL_WEEK_W
// (= 7 × the capacity day width).
const TASK_COL_W = RAIL_GUTTER_W;
const WEEK_COL_W = RAIL_WEEK_W;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Compute the sorted list of BST week-start Saturdays spanning all tasks. */
function computeWeekColumns(tasks: TaskWithBookings[]): string[] {
  let minStart: string | null = null;
  let maxEnd: string | null = null;
  for (const t of tasks) {
    if (t.StartDate && (!minStart || t.StartDate < minStart)) minStart = t.StartDate;
    if (t.EndDate && (!maxEnd || t.EndDate > maxEnd)) maxEnd = t.EndDate;
  }
  if (!minStart || !maxEnd) return [];
  return weekStartsInRange(minStart, maxEnd);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Phase2WeeklyGrid: React.FC<Phase2WeeklyGridProps> = ({
  tasks,
  assignmentMap,
  scenarioKey,
  onAssignmentsChanged,
  onAllocationChanged,
  compareToBaseline,
  hoveredWeek,
  onHoverWeek,
  holidays,
}) => {
  const theme = useTheme();
  // Shared "beam": the sage left/right rails + tint a hovered week gets, so it reads
  // as one highlighted column continuing down into the capacity grid.
  const beamRail = `2px solid ${theme.schedule.selection}`;

  // ── Derived column list (re-computed when tasks change) ───────────────────
  const weekColumns = computeWeekColumns(tasks);

  // ── Baseline assignment data (6C.6 — fetched only when compareToBaseline=true) ──
  // Shared with the Board grid via useBaselineAssignments.
  const baselineAssignmentMap = useBaselineAssignments(tasks, compareToBaseline);

  // ── Dialog escalation (SkillGate / Overbook) ──────────────────────────────
  const [dialogTask, setDialogTask] = useState<TaskWithBookings | null>(null);
  const [dialogPreselect, setDialogPreselect] = useState<AssignableUser | null>(null);
  // Per-machine staffing (079): the machine the escalating picker was staffing,
  // carried through so the dialog binds the resolved assignment to it.
  const [dialogBookingKey, setDialogBookingKey] = useState<string | null>(null);

  const handleEscalateToDialog = useCallback(
    (task: TaskWithBookings, preselectUser?: AssignableUser, machineBookingKey?: string | null) => {
      setDialogTask(task);
      setDialogPreselect(preselectUser ?? null);
      setDialogBookingKey(machineBookingKey ?? null);
    },
    [],
  );

  const handleCloseDialog = useCallback(() => {
    setDialogTask(null);
    setDialogPreselect(null);
    setDialogBookingKey(null);
  }, []);

  const handleDialogSaved = useCallback(
    (_count: number) => {
      if (dialogTask) onAssignmentsChanged(dialogTask.ScheduleTaskKey);
    },
    [dialogTask, onAssignmentsChanged],
  );

  const handleDialogAssigneesChanged = useCallback(() => {
    if (dialogTask) onAssignmentsChanged(dialogTask.ScheduleTaskKey);
  }, [dialogTask, onAssignmentsChanged]);

  // ── Empty state ───────────────────────────────────────────────────────────
  if (tasks.length === 0) return null;

  if (weekColumns.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: 'center',
          color: 'text.secondary',
          border: `1px dashed ${theme.border.color}`,
          borderRadius: `${theme.border.radius}px`,
        }}
      >
        <DateRangeIcon sx={{ fontSize: 36, opacity: 0.3 }} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Assign machines to tasks in Phase 1 to see the weekly schedule here.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* AssignEmployeesDialog — escalation from InlineAssigneePicker inside cells */}
      <AssignEmployeesDialog
        open={dialogTask !== null}
        task={dialogTask}
        preselectUser={dialogPreselect}
        onClose={handleCloseDialog}
        onSaved={handleDialogSaved}
        onAssigneesChanged={handleDialogAssigneesChanged}
        scenarioKey={scenarioKey ?? undefined}
        machineBookingKey={dialogBookingKey}
      />

      <Box
        onMouseLeave={() => onHoverWeek?.(null)}
        sx={{
          border: `1px solid ${theme.border.color}`,
          borderRadius: `${theme.border.radius}px`,
          // No own scroll — the shared rail container (Phase2Panel) provides ONE
          // horizontal scrollbar for both grids so they can't desync (2026-06-23).
          overflow: 'visible',
          backgroundColor: theme.palette.background.paper,
          width: 'fit-content',
        }}
      >
        {/* No header row here — the shared sticky Phase2RailHeader (in Phase2Panel)
            renders the WEEK labels for BOTH grids so they line up + stay pinned. */}
        <Table
          size="small"
          sx={{ tableLayout: 'fixed', width: TASK_COL_W + weekColumns.length * WEEK_COL_W }}
        >
          <TableBody>
            {tasks.map((task, rowIdx) => {
              const taskAssignments = assignmentMap[task.ScheduleTaskKey] ?? [];
              const taskBaselineAssignments = baselineAssignmentMap[task.ScheduleTaskKey];
              const rowBg = rowIdx % 2 === 1 ? theme.table.stripeBg : undefined;

              return (
                <TableRow
                  key={task.ScheduleTaskKey}
                  sx={{ '& td': { verticalAlign: 'top' }, backgroundColor: rowBg }}
                >
                  {/* Task name (sticky) */}
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      backgroundColor: rowBg ?? theme.palette.background.paper,
                      // Frozen-column divider via inset shadow — a borderRight collapses
                      // away when content scrolls under the sticky gutter (2026-06-24).
                      boxShadow: `inset -1px 0 0 0 ${theme.border.color}, 6px 0 6px -6px ${alpha(theme.palette.text.primary, 0.18)}`,
                      py: 1,
                      px: 1.5,
                      width: TASK_COL_W,
                    }}
                  >
                    <Tooltip title={task.TaskTypeName ?? ''} placement="right">
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{ fontSize: '0.82rem' }}
                          noWrap
                        >
                          {displayTaskName(task.Name, task.TaskTypeName)}
                        </Typography>
                        {showTypeSubtitle(task.Name, task.TaskTypeName) && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {task.TaskTypeName}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  </TableCell>

                  {/* One cell per week */}
                  {weekColumns.map((ws) => {
                    const hot = hoveredWeek === ws;
                    return (
                    <TableCell
                      key={ws}
                      onMouseEnter={() => onHoverWeek?.(ws)}
                      sx={{
                        px: 0.5,
                        py: 0.5,
                        width: WEEK_COL_W,
                        verticalAlign: 'top',
                        borderRight: `1px solid ${theme.border.color}`,
                        ...(hot ? { backgroundColor: alpha(theme.schedule.selection, 0.06), borderLeft: beamRail, borderRight: beamRail } : {}),
                        transition: 'background-color 0.12s',
                      }}
                    >
                      <Phase2WeeklyCell
                        task={task}
                        weekStart={ws}
                        assignments={taskAssignments}
                        scenarioKey={scenarioKey}
                        onAssignmentsChanged={onAssignmentsChanged}
                        onAllocationChanged={onAllocationChanged}
                        onEscalateToDialog={handleEscalateToDialog}
                        baselineAssignments={taskBaselineAssignments}
                        compareToBaseline={compareToBaseline}
                        holidays={holidays}
                      />
                    </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </>
  );
};
